/**
 * Toast notification module.
 * Creates fragno-toast elements imperatively. The fragno-toast custom element
 * is registered globally by whichever fragno pack the request loads
 * (ApplicationController#fragno_pack) — this module just creates and manages
 * the DOM nodes. Shop modules never import from "fragno" by name: a named
 * import binds to the fragno_latest copy specifically, which would run even
 * on a fragno_next page. Reaching fragno only through the already-registered
 * custom elements keeps every shop module variant-agnostic.
 *
 * Usage:
 *   import { showToast } from "../modules/toast";
 *   showToast({ message: "Saved!", tone: "success" });
 */

const HOST_ID = "fragno-toast-host";
const DEFAULT_DURATION = 4000;

/**
 * @param {object} [options]
 * @param {string} [options.message]  Primary message text.
 * @param {string} [options.tone]     "success" | "error" | "informational" | "alert" | "neutral"
 * @param {number} [options.duration] Auto-dismiss in ms (default 4000, 0 to disable).
 */
export function showToast({
  message = "",
  tone = "informational",
  duration = DEFAULT_DURATION,
} = {}) {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    host.className = "fragno-toast-host";
    host.setAttribute("aria-live", "polite");
    host.dataset.position = "bottom";
    document.body.appendChild(host);
  }

  const el = document.createElement("fragno-toast");
  el.setAttribute("message", message);
  el.setAttribute("tone", tone);
  host.appendChild(el);
  el.setAttribute("open", "");

  const finish = () => {
    el.removeAttribute("open");
    if (el.isConnected) el.remove();
    if (host.childElementCount === 0 && host.parentNode) host.remove();
  };

  el.addEventListener("fragno-toast-dismiss", finish, { once: true });

  if (duration > 0) {
    window.setTimeout(finish, duration);
  }
}
